<sui-dialog-simple>
  <ng-template suiDialogHeader>
    <h2 ngpDialogTitle class="modal-title">
      {{ "auto-story-tool.modal.title" | translate }}
    </h2>
    <sui-button
      size="sm"
      variant="danger"
      shape="round"
      [leadingIcon]="['fas', 'times']"
      [iconOnly]="true"
      ariaLabel="Close"
      (click)="onCancel()"
    ></sui-button>
  </ng-template>

  <ng-template suiDialogContent>
    @if (autoStoryForm) {
      <form [formGroup]="autoStoryForm">
        <!-- Main Form Section -->
        <div class="row mb-3">
          <div class="col-12">
            <label for="prompt_template_id" sui-label>{{
              "auto-story-tool.form.select-prompt-label" | translate
            }}</label>
            <sui-combobox
              id="prompt_template_id"
              formControlName="prompt_template_id"
              [options]="promptOptions()"
              [placeholder]="
                'auto-story-tool.form.select-prompt-default-option' | translate
              "
            ></sui-combobox>
          </div>
        </div>
        <div class="preview-container">
          <label sui-label>{{
            "auto-story-tool.form.prompt-template-preview" | translate
          }}</label>
          <sui-textarea
            [rows]="5"
            [readonly]="true"
            [ngModel]="getSelectedPromptPreview()"
            [ngModelOptions]="{ standalone: true }"
          ></sui-textarea>
        </div>
        <!-- Model Selection Section - restricted to global admins -->
        @if (isGlobalAdmin()) {
          <hr class="form-section-separator" />
          <div class="row mb-3">
            <div class="col-12">
              <label for="model" sui-label>{{
                "auto-story-tool.form.model-label" | translate
              }}</label>
              <sui-combobox
                id="model"
                formControlName="model"
                [options]="modelOptions()"
                [placeholder]="
                  'auto-story-tool.models.default-from-prompt' | translate
                "
              ></sui-combobox>
            </div>
          </div>
          @if (effectiveModel(); as model) {
            <dl class="model-preview">
              <dt>
                {{ "auto-story-tool.model-preview.llm-model" | translate }}
              </dt>
              <dd>
                {{ model.name }}
                @if (isModelFromPrompt()) {
                  <span class="model-preview__origin">{{
                    "auto-story-tool.model-preview.from-the-selected-prompt"
                      | translate
                  }}</span>
                }
              </dd>
              @if (model.description) {
                <dt>
                  {{ "auto-story-tool.model-preview.description" | translate }}
                </dt>
                <dd>{{ model.description }}</dd>
              }
              <dt>
                {{
                  "auto-story-tool.model-preview.price-per-1m-input-tokens"
                    | translate
                }}
              </dt>
              <dd>
                {{
                  model.price_input
                    | currency: model.price_currency_code : "symbol" : "1.2-4"
                }}
              </dd>
              <dt>
                {{
                  "auto-story-tool.model-preview.price-per-1m-output-tokens"
                    | translate
                }}
              </dt>
              <dd>
                {{
                  model.price_output
                    | currency: model.price_currency_code : "symbol" : "1.2-4"
                }}
              </dd>
            </dl>
          } @else if (unavailableModelId(); as unavailableModel) {
            <p class="model-preview__warning" role="alert">
              {{
                "auto-story-tool.model-preview.this-model-is-no-longer-available"
                  | translate: { model: unavailableModel }
              }}
            </p>
          }
        }
        <hr class="form-section-separator" />
        <!-- Prompt Parameters Section - dynamically generated -->
        @if (hasPromptParameters) {
          <div class="prompt-parameters-section">
            <h6 class="section-heading">
              {{ "auto-story-tool.form.prompt-parameters" | translate }}
            </h6>
            <div [formGroup]="promptParametersForm">
              @for (control of promptParameterControls; track control) {
                <div class="row mb-3">
                  <div class="col-12">
                    <label [for]="control.key" sui-label>{{
                      getParameterLabel(control.key)
                    }}</label>
                    <!-- IMPORTANT: currently presume that we have two fields, if we need -> make it more flexible -->
                    @if (control.type === "number") {
                      <sui-input
                        [id]="control.key"
                        type="number"
                        [formControlName]="control.key"
                        [min]="control.min"
                        [max]="control.max"
                      ></sui-input>
                    }
                    <!-- Select for Nomenclature type -->
                    @if (control.type === "select") {
                      <sui-select
                        [id]="control.key"
                        [formControlName]="control.key"
                      >
                        @for (option of control.options; track option) {
                          <sui-select-option
                            [value]="option.id"
                            [label]="option.name"
                            >{{ option.name }}</sui-select-option
                          >
                        }
                      </sui-select>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }
        <!-- Algorithm Settings Section -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label for="clusters" sui-label>{{
              "auto-story-tool.form.max-stories-label" | translate
            }}</label>
            <sui-input
              id="clusters"
              type="number"
              formControlName="clusters"
              [min]="1"
              [max]="250"
            ></sui-input>
          </div>
          <div class="col-md-6">
            <label for="distance_threshold" sui-label>{{
              "auto-story-tool.form.max-distance-label" | translate
            }}</label>
            <sui-input
              id="distance_threshold"
              type="number"
              formControlName="distance_threshold"
              [min]="1"
              [max]="100"
            ></sui-input>
          </div>
        </div>
        <!-- Checkboxes Section -->
        <div class="row mb-3">
          <div class="col-md-6">
            <sui-checkbox formControlName="sanitize_mentions">
              {{ "auto-story-tool.form.remove-mentions-label" | translate }}
            </sui-checkbox>
          </div>
          <div class="col-md-6">
            <sui-checkbox formControlName="sanitize_urls">
              {{ "auto-story-tool.form.remove-urls-label" | translate }}
            </sui-checkbox>
          </div>
        </div>
        <!-- Dropdowns Section -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label for="sort" sui-label>
              {{
                "auto-story-tool.form.story-performance-label" | translate
              }}</label
            >
            <sui-select id="sort" formControlName="sort">
              @for (field of sortFields; track field) {
                <sui-select-option [value]="field.id" [label]="field.name">{{
                  field.name
                }}</sui-select-option>
              }
            </sui-select>
          </div>
          <div class="col-md-6">
            <label for="sample_metric" sui-label>{{
              "auto-story-tool.form.sampling-metrict-label" | translate
            }}</label>
            <sui-select id="sample_metric" formControlName="sample_metric">
              @for (metric of sampleMetrics; track metric) {
                <sui-select-option [value]="metric.id" [label]="metric.name">{{
                  metric.name
                }}</sui-select-option>
              }
            </sui-select>
          </div>
        </div>
        @if (showSchedule) {
          <div class="row mb-3">
            <div class="col-12">
              <app-algorithm-schedule
                [parent]="autoStoryForm"
                [editMode]="editMode"
                [defaultIntervalMinutes]="initialIntervalMinutes"
                [initialRecurrence]="initialRecurrence"
                [initialTimezoneId]="initialTimezoneId"
              ></app-algorithm-schedule>
            </div>
          </div>
        }
        @if (sampleMetricId != "") {
          <div class="row mb-3 sampling-controls">
            <div class="col-md-6">
              <label for="sampling_percentile_groups" sui-label>{{
                "auto-story-tool.form.sampling-percentile-groups" | translate
              }}</label>
              <sui-input
                id="sampling_percentile_groups"
                type="number"
                formControlName="sampling_percentile_groups"
                [min]="1"
                [max]="10"
              ></sui-input>
            </div>
            <div class="col-md-6">
              <label for="sampling_percentage_per_group" sui-label>{{
                "auto-story-tool.form.sampling-percentage-per-group" | translate
              }}</label>
              <sui-input
                id="sampling_percentage_per_group"
                type="number"
                formControlName="sampling_percentage_per_group"
                [min]="1"
                [max]="100"
              ></sui-input>
            </div>
          </div>
          <div class="row mb-3 sampling-controls">
            <div class="col-md-6">
              <label for="sample_length" sui-label>{{
                "auto-story-tool.form.sampling-minimum-content-length"
                  | translate
              }}</label>
              <sui-input
                id="sample_length"
                type="number"
                formControlName="sample_length"
                [min]="0"
              ></sui-input>
            </div>
            <div class="col-md-6">
              <sui-checkbox formControlName="sampling_time_buckets">
                {{
                  "auto-story-tool.form.bucket-by-time-published" | translate
                }}
              </sui-checkbox>
            </div>
          </div>
        }
        <hr class="form-section-separator" />
        <!-- Delivery Section - the final pipeline stages -->
        <div class="row mb-3">
          <div class="col-md-6">
            <sui-checkbox formControlName="create_story_posts">
              {{ "auto-story-tool.form.create-story-posts-label" | translate }}
            </sui-checkbox>
          </div>
          <div class="col-md-6">
            <sui-checkbox formControlName="send_notification">
              {{ "auto-story-tool.form.send-notification-label" | translate }}
            </sui-checkbox>
          </div>
        </div>
      </form>
    }
  </ng-template>

  <ng-template suiDialogFooter>
    <sui-button variant="secondary" (click)="onCancel()">
      {{ "auto-story-tool.form-button.cancel" | translate }}
    </sui-button>
    <sui-button
      variant="primary"
      [leadingIcon]="runButtonIcon()"
      [disabled]="!autoStoryForm?.valid"
      (click)="onSubmit()"
    >
      {{ runButtonText() }}
    </sui-button>
  </ng-template>
</sui-dialog-simple>
